import { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DialogContent, DialogActions } from '@mui/material';
import { useAuth } from '@app/api/auth/useAuth';
import { useEmail } from '@app/api/profile/useEmail';
import { useDialogContext } from '@app/components/dialogs/DialogProvider';
import { ResendButton, ThemeButton } from '@shared/ui/buttons';
import { CodePinInput } from '@shared/ui/forms/inputs';
import { validateServerErrors } from '@utils/validation';

const COOLDOWN_DURATION = 180; // seconds

function getCooldownExpiry() {
  return new Date(Date.now() + COOLDOWN_DURATION * 1000).toISOString();
}

export default function EmailCheckCodeForm({
  setStep,
  isNew,
  newEmail,
  close,
}) {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { setSubtitle, setTitle } = useDialogContext();
  const [expiredAt, setExpiredAt] = useState(getCooldownExpiry);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const { logoutUser } = useAuth();
  const { verifyOldEmail, verifyNewEmail, sendNewEmail } = useEmail();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm();

  async function onSubmit(formData) {
    try {
      if (isNew) {
        const res = await verifyNewEmail({
          pin: formData.pin,
          new_email: newEmail,
        });
        if (res) {
          close();
          await logoutUser();
        }
      } else {
        const res = await verifyOldEmail(formData);
        if (res) setStep(3);
      }
    } catch (err) {
      validateServerErrors(setError, err);
    }
  }

  async function resendCode() {
    try {
      setIsResendLoading(true);
      const res = await sendNewEmail({ new_email: newEmail });
      if (res) setExpiredAt(getCooldownExpiry());
    } catch (error) {
      validateServerErrors(setError, error);
    } finally {
      setIsResendLoading(false);
    }
  }

  useLayoutEffect(() => {
    if (isNew) {
      setTitle('new_email');
    }
    setSubtitle(isNew ? newEmail : user.email);
  }, []);

  return (
    <>
      <DialogContent>
        <div className="form">
          <CodePinInput
            control={control}
            name="pin"
            error={errors.pin}
            label={t('enter_email_code')}
          />
          <ResendButton
            label={t('didnt_receive_code')}
            expiredAt={expiredAt}
            isLoading={isResendLoading}
            onResend={() => (isNew ? resendCode() : setStep(1))}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <ThemeButton
          onClick={handleSubmit(onSubmit)}
          endIcon={isNew ? 'check' : 'chevron-right'}
          loading={isSubmitting}
        >
          {t(isNew ? 'confirm' : 'continue')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
